/**
 * Fallback de permissão no front: garante que eventos de ticket recebidos via socket
 * (enquanto o backend ainda puder emitir em namespace amplo) só sejam exibidos/inseridos
 * quando o usuário atual tiver permissão.
 * Pode ser removido quando todos os emits do backend forem por rooms (user/queue/company-all).
 *
 * @param {Object} currentUser - usuário logado (id, profile, allUserChat, whatsappId, queues)
 * @param {Object} ticket - ticket do evento (id, queueId, userId, status, whatsappId, ...)
 * @param {number[]} [allowedQueueIds] - IDs das filas permitidas (ex.: user.queues.map(q => q.id))
 * @returns {boolean} true se o usuário pode ver o ticket
 */
export function canSeeTicket(currentUser, ticket, allowedQueueIds = null) {
  if (!currentUser || !ticket) return false;

  const profile = (currentUser.profile || '').toLowerCase();
  const allUserChat = (currentUser.allUserChat || '').toLowerCase();
  const queueIds =
    Array.isArray(allowedQueueIds) && allowedQueueIds.length > 0
      ? allowedQueueIds
      : currentUser.queues?.map((q) => q.id) || [];

  // Admin ou allUserChat: aceitar sempre
  if (profile === 'admin' || allUserChat === 'enabled') {
    return true;
  }

  // Perfil user: aplicar regras de restrição
  // Automação sem fila nem usuário: ignorar
  if (ticket.queueId == null && ticket.userId == null) {
    return false;
  }

  // Filas: se o user tem filas, o ticket deve estar em uma delas
  if (queueIds.length > 0 && ticket.queueId != null) {
    if (!queueIds.includes(ticket.queueId)) {
      return false;
    }
  }

  // Restrição de WhatsApp: se o user tem whatsappId, o ticket deve ser desse canal
  if (currentUser.whatsappId != null && currentUser.whatsappId !== '') {
    if (ticket.whatsappId != null && ticket.whatsappId !== currentUser.whatsappId) {
      return false;
    }
  }

  const status = (ticket.status || '').toLowerCase();
  const ticketUserId = ticket.userId;

  if (status === 'open' || status === 'closed') {
    return ticketUserId === currentUser.id;
  }

  if (status === 'pending') {
    return ticketUserId == null || ticketUserId === currentUser.id;
  }

  // Outros status (ex.: group, lgpd, nps): permitir se for do usuário ou sem dono
  return ticketUserId == null || ticketUserId === currentUser.id;
}

/**
 * Determina se o usuário deve receber notificação (som + desktop) para um ticket.
 * Mais restrito que canSeeTicket: admin vê tudo mas só é notificado do que é dele.
 *
 * Regras:
 * - Ticket com userId → só notifica o usuário atribuído
 * - Ticket só com queueId → notifica quem pertence à fila
 * - Sem fila nem usuário → notifica apenas admin (ticket ainda não triado)
 *
 * @param {Object} currentUser - usuário logado
 * @param {Object} ticket - ticket do evento
 * @returns {boolean}
 */
export function shouldNotifyForTicket(currentUser, ticket) {
  if (!currentUser || !ticket) return false;

  const userQueues = currentUser.queues?.map((q) => q.id) || [];
  const isAdmin = (currentUser.profile || '').toLowerCase() === 'admin';

  // Ticket atribuído a um usuário específico → só esse usuário é notificado
  if (ticket.userId != null) {
    return ticket.userId === currentUser.id;
  }

  // Ticket só com fila → notifica quem pertence à fila
  if (ticket.queueId != null) {
    return userQueues.includes(ticket.queueId);
  }

  // Sem fila nem usuário → notifica apenas admin (ticket ainda não triado)
  return isAdmin;
}
